import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, MapPin, Clock, Users, Compass, Heart, Leaf, MessageCircle, Sparkles, GraduationCap, Calendar } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, waLink } from "@/lib/api";

const HERO_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_bufnita-melissa/artifacts/9xrg8exk_HABITAT%20ALL.png";

export default function Home() {
  const [tours, setTours] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/tours").then(r => setTours(r.data.slice(0, 3))).catch(() => {});
    api.get("/animals", { params: { featured: true } }).then(r => setAnimals(r.data.slice(0, 6))).catch(() => {});
    api.get("/faqs").then(r => setFaqs(r.data.slice(0, 6))).catch(() => {});
    api.get("/reviews").then(r => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[80vh] lg:min-h-0 lg:aspect-[1672/941] flex items-end -mt-16 lg:-mt-20 pt-24 pb-16 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Bufnița Melissa la Habitatul Meu" className="w-full h-full object-cover object-[center_25%] lg:object-contain lg:object-center" />
          {/* Umbră concentrată doar la baza secțiunii pentru lizibilitate — restul imaginii rămâne curat */}
          <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-forest-900 via-forest-900/55 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-forest-900/60 to-transparent" />
        </div>
        <div className="relative max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 w-full text-cream-50 section-fade [text-shadow:0_2px_18px_rgba(9,32,25,0.55)]">
          <p data-testid="hero-eyebrow" className="text-xs uppercase tracking-[0.25em] font-bold text-tucan mb-4">Dumești · La aproximativ 30 de minute de Iași</p>
          <h1 data-testid="hero-title" className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl text-balance">
            Aici nu privești doar animalele.<br /><em className="text-tucan not-italic font-semibold">Le cunoști povestea.</em>
          </h1>
          <p data-testid="hero-subtitle" className="mt-6 text-lg lg:text-xl text-cream-50/90 max-w-2xl leading-relaxed">
            Descoperă păsări exotice, capibara, alpaca, canguri și multe alte animale într-un tur ghidat de aproximativ 60 de minute, cu prezentare, interacțiune controlată și hrană permisă inclusă.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/tururi" data-testid="hero-cta-book" className="bg-tucan hover:bg-[#D97A29] text-white rounded-full px-8 py-4 font-semibold flex items-center gap-2 transition-colors">
              Rezervă un tur <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/animale" data-testid="hero-cta-animals" className="border-2 border-cream-50 text-cream-50 hover:bg-cream-50 hover:text-forest-900 rounded-full px-8 py-4 font-semibold transition-colors">
              Descoperă animalele
            </Link>
          </div>
          <p className="mt-6 text-sm text-cream-50/70 max-w-2xl">
            Accesul se face numai pe bază de tur programat. Copiii 0–3 ani și copiii cu dizabilități beneficiază de intrare gratuită.
          </p>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-cream-100 py-8 border-y border-forest-900/10">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { icon: Star, label: "4,9/5 evaluare", sub: "vizitatori mulțumiți" },
            { icon: MapPin, label: "~30 min", sub: "de Iași" },
            { icon: Clock, label: "~60 min", sub: "durata turului" },
            { icon: Leaf, label: "Hrana inclusă", sub: "oferită de parc" },
          ].map((s, i) => (
            <div key={i} data-testid={`trust-stat-${i}`} className="flex flex-col items-center gap-1">
              <s.icon className="w-6 h-6 text-turaco" />
              <div className="font-serif text-xl text-forest-900">{s.label}</div>
              <div className="text-xs text-muted">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* NEXT TOURS */}
      <section className="py-16 lg:py-24 feather-pattern">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-turaco mb-3">Următoarele tururi</p>
            <h2 className="font-serif text-3xl lg:text-5xl text-forest-900 tracking-tight">Alege următoarea întâlnire cu lumea animalelor</h2>
            <p className="mt-4 text-muted leading-relaxed">Tururile sunt organizate în zile și la ore anunțate. Alege intervalul potrivit și rezervă înainte ca locurile să se ocupe.</p>
          </div>
          {tours.length === 0 ? (
            <div className="bg-cream-50 rounded-3xl p-8 text-center">
              <p className="text-muted mb-4">Următoarele date sunt în pregătire.</p>
              <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex bg-[#25D366] text-white rounded-full px-6 py-3 font-semibold gap-2 items-center">
                <MessageCircle className="w-4 h-4" /> Anunță-mă pe WhatsApp
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tours.map(t => (
                <div key={t.id} data-testid={`tour-card-${t.id}`} className="bg-cream-50 rounded-3xl p-6 border border-forest-900/5 hover:-translate-y-1 transition-transform">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs uppercase tracking-wider font-bold text-turaco">Tur ghidat</span>
                    {t.seats_available > 0 && t.seats_available <= 3 && (
                      <span className="bg-tucan/10 text-tucan text-xs px-2 py-1 rounded-full font-semibold">Ultimele {t.seats_available}</span>
                    )}
                    {t.seats_available === 0 && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-semibold">Complet</span>
                    )}
                  </div>
                  <div className="font-serif text-2xl text-forest-900 mb-1">{new Date(t.date).toLocaleDateString('ro-RO', {weekday: 'long', day: 'numeric', month: 'long'})}</div>
                  <div className="text-forest-800 font-semibold">{t.start_time} · {t.duration_minutes} min</div>
                  <div className="mt-4 text-sm text-muted">De la <span className="text-forest-900 font-semibold">{t.price_adult} RON</span> / persoană</div>
                  <div className="mt-1 text-xs text-muted">{t.seats_available} locuri disponibile</div>
                  <Link to={`/rezervare?tour=${t.id}`} data-testid={`book-tour-${t.id}`} className="mt-4 w-full bg-forest-900 hover:bg-forest-800 text-cream-50 rounded-full py-3 font-semibold flex items-center justify-center gap-2 transition">
                    Rezervă <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          )}
          <div className="mt-8">
            <Link to="/tururi" className="text-forest-900 font-semibold underline underline-offset-4 hover:text-tucan">Vezi toate tururile →</Link>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATOR */}
      <section className="py-16 lg:py-24 bg-forest-900 text-cream-50">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-tucan mb-3">Mai mult decât o vizită</p>
            <h2 className="font-serif text-3xl lg:text-5xl tracking-tight">Fiecare pasăre, fiecare animal, fiecare întâlnire are o poveste.</h2>
            <p className="mt-6 text-cream-50/80 leading-relaxed">
              La Habitatul Meu nu te plimbi anonim printre țarcuri. Ești însoțit, asculți poveștile animalelor, afli lucruri surprinzătoare despre ele și înveți cum să te apropii cu grijă și respect.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Compass, title: "Descoperă", text: "Afli cine sunt locuitorii Habitatului și de unde provin." },
                { icon: Users, title: "Interacționează", text: "Te apropii în condiții controlate, respectând starea animalelor." },
                { icon: Heart, title: "Păstrează povestea", text: "Pleci cu amintiri, fotografii și mai multă iubire pentru natură." },
              ].map((s, i) => (
                <div key={i} className="flex gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-full bg-tucan/20 border border-tucan/40 flex items-center justify-center"><s.icon className="w-5 h-5 text-tucan" /></div>
                  <div>
                    <div className="font-serif text-xl">{s.title}</div>
                    <div className="text-sm text-cream-50/70">{s.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-[32px] overflow-hidden aspect-[4/5]">
            <img src="https://customer-assets.emergentagent.com/job_685c66be-4abf-4d5e-89f0-a841bac5901e/artifacts/kk15k30y_118.webp" alt="Wallaby la Habitatul Meu" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* FEATURED ANIMALS */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-turaco mb-3">Locuitorii Habitatului</p>
              <h2 className="font-serif text-3xl lg:text-5xl text-forest-900 tracking-tight">Cunoaște-i pe cei care te așteaptă</h2>
              <p className="mt-4 text-muted">Unii au pene spectaculoase. Alții sar, torc, ronțăie sau te privesc cu o curiozitate imposibil de uitat.</p>
            </div>
            <Link to="/animale" className="text-forest-900 font-semibold underline underline-offset-4">Vezi toate animalele →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {animals.map(a => (
              <Link key={a.id} to={`/animale/${a.slug}`} data-testid={`animal-card-${a.slug}`} className="group relative rounded-3xl overflow-hidden aspect-[4/5] bg-forest-900">
                {a.image_url ? (
                  <img src={a.image_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-forest-800 to-forest-900 flex items-center justify-center text-cream-50/40"><Leaf className="w-16 h-16" /></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-forest-900 via-forest-900/40 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-cream-50">
                  <div className="text-xs uppercase tracking-wider text-tucan font-semibold">{a.common_name}</div>
                  <div className="font-serif text-2xl">{a.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 lg:py-24 bg-cream-100">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl lg:text-5xl text-forest-900 tracking-tight max-w-3xl">Patru pași simpli până la întâlnirea voastră</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {["Alegi data și ora turului.", "Selectezi numărul de participanți.", "Completezi datele de contact și confirmi regulile.", "Primești confirmarea și toate informațiile utile."].map((s, i) => (
              <div key={i} className="bg-cream-50 rounded-3xl p-6">
                <div className="font-serif text-6xl text-tucan/30">{String(i+1).padStart(2, '0')}</div>
                <div className="mt-3 text-forest-900 font-medium">{s}</div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/tururi" className="inline-flex bg-tucan text-white rounded-full px-8 py-4 font-semibold items-center gap-2">Vezi tururile disponibile <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* SCHOOLS */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[32px] overflow-hidden bg-forest-800 p-8 lg:p-16 text-cream-50">
            <div className="absolute inset-0 opacity-20"><img src="https://customer-assets.emergentagent.com/job_685c66be-4abf-4d5e-89f0-a841bac5901e/artifacts/a8zp21ep_121.jpg" className="w-full h-full object-cover" alt="" /></div>
            <div className="relative max-w-2xl">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-tucan mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Școala Altfel · Săptămâna Verde</p>
              <h2 className="font-serif text-3xl lg:text-5xl tracking-tight">O lecție vie pe care copiii nu o uită.</h2>
              <p className="mt-4 text-cream-50/85 leading-relaxed">Transformăm curiozitatea într-o experiență educativă autentică. Copiii descoperă specii exotice, comportamente, hrană, habitate și reguli de apropiere responsabilă față de animale.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/scoli-si-grupuri" data-testid="schools-cta" className="bg-tucan text-white rounded-full px-6 py-3 font-semibold">Solicită o vizită de grup</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="py-16 lg:py-24 bg-cream-100">
          <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif text-3xl lg:text-5xl text-forest-900 tracking-tight text-center max-w-3xl mx-auto">Ce spun oamenii după ce ne cunosc</h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.slice(0,3).map(r => (
                <div key={r.id} className="bg-cream-50 rounded-3xl p-6">
                  <div className="flex text-tucan mb-3">{[...Array(r.rating)].map((_,i)=><Star key={i} className="w-4 h-4 fill-current" />)}</div>
                  <p className="text-forest-900 leading-relaxed">"{r.quote}"</p>
                  <p className="mt-4 text-sm font-semibold text-muted">— {r.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STORY / SUPPORT */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-forest-900 text-cream-50 rounded-[32px] p-8 lg:p-12">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-tucan mb-3">Povestea Habitatului</p>
            <h3 className="font-serif text-3xl lg:text-4xl tracking-tight">Un loc crescut din pasiune, grijă și mii de ore petrecute alături de animale.</h3>
            <p className="mt-4 text-cream-50/80">Habitatul Meu este o asociație din Dumești, Iași, dedicată apropierii oamenilor de lumea păsărilor exotice și a animalelor.</p>
            <Link to="/povestea-noastra" className="mt-6 inline-flex items-center gap-2 text-tucan font-semibold">Descoperă povestea noastră <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="bg-tucan text-white rounded-[32px] p-8 lg:p-12">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-white mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4" /> Susține Habitatul</p>
            <h3 className="font-serif text-3xl lg:text-4xl tracking-tight">Ajută-ne să continuăm să le oferim grijă, hrană și un loc potrivit.</h3>
            <p className="mt-4 text-white/90">Fiecare bilet, donație sau adopție simbolică susține activitatea asociației și îngrijirea zilnică a animalelor.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/sustine-ne" className="bg-white text-tucan rounded-full px-6 py-3 font-semibold">Donează</Link>
              <Link to="/adopta-simbolic" className="border-2 border-white text-white rounded-full px-6 py-3 font-semibold">Adoptă simbolic</Link>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="py-16 lg:py-24 bg-cream-100">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-turaco mb-3">Locație</p>
            <h2 className="font-serif text-3xl lg:text-5xl text-forest-900 tracking-tight">O lume diferită, aproape de Iași</h2>
            <p className="mt-4 text-muted">Ne găsești în Dumești, județul Iași. Înainte de plecare, verifică ora turului și confirmarea rezervării.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://maps.google.com/?q=47.17939,27.34896" target="_blank" rel="noreferrer" className="border-2 border-forest-900 text-forest-900 rounded-full px-6 py-3 font-semibold">Google Maps</a>
              <a href="https://waze.com/ul?ll=47.17939,27.34896" target="_blank" rel="noreferrer" className="border-2 border-forest-900 text-forest-900 rounded-full px-6 py-3 font-semibold">Waze</a>
              <a href={waLink()} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white rounded-full px-6 py-3 font-semibold flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
          </div>
          <div className="rounded-[32px] overflow-hidden aspect-video bg-forest-900">
            <iframe title="Harta Habitatul Meu" src="https://maps.google.com/maps?q=47.17939,27.34896&z=13&output=embed" className="w-full h-full border-0" loading="lazy" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl lg:text-5xl text-forest-900 tracking-tight text-center">Întrebări frecvente</h2>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((f, i) => (
              <AccordionItem key={f.id} value={`f${i}`} data-testid={`faq-${i}`} className="border-b border-forest-900/10">
                <AccordionTrigger className="text-left font-serif text-lg text-forest-900 hover:no-underline py-5">{f.question}</AccordionTrigger>
                <AccordionContent className="text-muted leading-relaxed pb-5">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-10 text-center">
            <a href={waLink()} target="_blank" rel="noreferrer" className="text-forest-900 font-semibold underline underline-offset-4">Mai ai o întrebare? Scrie-ne pe WhatsApp →</a>
          </div>
        </div>
      </section>
    </div>
  );
}
